import React from 'react';
import { useSelector } from 'react-redux';
import { useTranslate } from 'hooks/useTranslate';
import MainLayout from 'layouts/MainLayout';
// ServiceMessage is statically imported by ErrorBoundary, which is on the
// home critical path. Replacing Typography with a styled <h1>/<h2> keeps
// the @mui/material chunk off the entry graph.
import { styled } from '@mui/system';

const Content = styled('section')(({ theme }) => ({
  padding: '40px 100px',
  ...theme.wrapper,
  minHeight: '100vh',
  [theme.breakpoints.down('lg')]: {
    padding: 50
  },
  [theme.breakpoints.down('md')]: {
    padding: 30
  },
  [theme.breakpoints.down('sm')]: {
    padding: 20
  }
}));

const Title = styled('h1')({
  margin: 0,
  marginBottom: 20,
  fontSize: 36,
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'center'
});

const SubTitle = styled('h2')({
  margin: 0,
  fontSize: 24,
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'center'
});

const ServiceMessage = ({ children }) => {
  const t = useTranslate('ERRORS');
  const error = useSelector((state) => state?.error?.serviceMessage || null);

  React.useEffect(() => {
    const loader = document.getElementById('init_preloader');
    const root = document.getElementById('root');

    // Reveal the page: un-hide #root and drop the skeleton overlay together,
    // so there is never a frame showing one without the other.
    const reveal = () => {
      if (root && root.style.display === 'none') root.style.display = '';
      if (typeof window !== 'undefined') delete window.__rootHiddenForSkeleton;
      loader && loader.remove();
    };

    // Home routes never raise the skeleton — nothing to wait for, just make
    // sure #root is visible.
    if (!loader) {
      reveal();
      return;
    }

    // #init_preloader is an opaque full-screen overlay. This effect fires the
    // instant ServiceMessage commits — but <Routes/> is its child and its
    // lazy route chunk is still loading, so #root is empty. Removing the
    // skeleton now uncovers a blank page for ~150ms until content paints; and
    // once it does paint, its web fonts may still be loading, so the first
    // frame renders in a fallback font and visibly swaps.
    //
    // Hold the skeleton until the page is genuinely ready, then reveal in one
    // step. #root stays visible *under* the overlay the whole time, so its
    // fonts load while the skeleton hides the work in progress.
    let cancelled = false;
    let rafId;

    // 1) Wait until a route commits real DOM into #root. Suspense fallbacks
    //    here render null, so a non-zero childElementCount reliably means
    //    actual content is on screen.
    const contentPainted = new Promise((resolve) => {
      const check = () => {
        if (cancelled) return;
        if (root && root.childElementCount > 0) resolve();
        else rafId = requestAnimationFrame(check);
      };
      check();
    });

    // 2) After content paints, give the browser two frames to lay it out and
    //    kick off its @font-face fetches, THEN wait on document.fonts.ready.
    //    Reading fonts.ready only now matters: read before content exists it
    //    resolves immediately (nothing pending) and the font swap still leaks.
    const ready = contentPainted
      .then(() => new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r))))
      .then(() =>
        typeof document !== 'undefined' && document.fonts && document.fonts.ready
          ? document.fonts.ready
          : Promise.resolve()
      );

    // Backstop: never trap the user behind the skeleton if a chunk or font
    // request stalls. 8s is well past a normal cold load.
    const safety = new Promise((resolve) => setTimeout(resolve, 8000));

    Promise.race([ready, safety]).then(() => {
      if (!cancelled) reveal();
    });

    return () => {
      cancelled = true;
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, []);

  if (!error) {
    return children;
  }

  return (
    <MainLayout title={t('Title')}>
      <Content>
        <Title variant="h1">{t(error.message)}</Title>
        <SubTitle variant="h2">{t(error.message + ' subtitle')}</SubTitle>
      </Content>
    </MainLayout>
  );
};

export default ServiceMessage;
